// A signup invite code carried in on the landing URL (`/sign-up?code=LAUNCH25` from the
// gizmodata.com "Start free" buttons). The sign-in/sign-up round-trip through Google /
// Microsoft / Apple / GitHub drops the query string, so the code is kept in localStorage
// until the first-organization form picks it up, and cleared once an org is created.

const STORAGE_KEY = 'gizmodata.signupCode';
const MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;
// Codes are short, human-friendly tokens (LAUNCH25, WELCOME-ACME) — anything else is ignored.
const CODE_RE = /^[A-Za-z0-9_-]{1,64}$/;

/** Save `?code=` from the current URL (if it looks like a code) and drop it from the address bar. */
export function captureSignupCodeFromUrl(): void {
  try {
    const url = new URL(window.location.href);
    const code = url.searchParams.get('code')?.trim();
    if (!code || !CODE_RE.test(code)) return;
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ code, savedAt: Date.now() }));
    url.searchParams.delete('code');
    window.history.replaceState(window.history.state, '', url.pathname + url.search + url.hash);
  } catch {
    // Storage blocked (private window, strict settings): the user can still type the code.
  }
}

/** The saved code, or '' when there is none (or it is older than 30 days). */
export function pendingSignupCode(): string {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return '';
    const { code, savedAt } = JSON.parse(raw) as { code?: string; savedAt?: number };
    if (!code || !CODE_RE.test(code) || !savedAt || Date.now() - savedAt > MAX_AGE_MS) {
      localStorage.removeItem(STORAGE_KEY);
      return '';
    }
    return code;
  } catch {
    return '';
  }
}

export function clearPendingSignupCode(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Nothing to clear.
  }
}
